import './MortgageBuyOrDie.css';

import React, { useCallback, useState } from 'react';
import classname from '@search/classname/src';
import { Modal, Button, Typography } from '@vtblife/uikit';
import { Rooms } from '@search/filter-enums/enums/Rooms';
import { useClassifiedOfferDataLayerPush } from '../../../../../domain/offer/analytics/ClassifiedOfferGtmContext';
import { MortgageBuyOrDieResultCard } from './subcomponents/MortgageBuyOrDieResultCard';
import { MortgageBuyOrDieReason } from './subcomponents/MortgageBuyOrDieReason';
import { getLocalStorageLastValue } from './helpers/getLocalStorageLastValue';
import { calculateBuyOrDie } from './helpers/calculateBuyOrDie';
import { MortgageBuyOrDieModalForm } from './subcomponents/MortgageBuyOrDieForm';
import { mapRegionToSelect } from './helpers/mapRegionToSelect';

// Немного другое название, потому что Андрей Африканов боится, что кто-то посмотрит наши классы
// ужас какой
export const cnMrtgBuyOrDie = classname.bind(null, 'MortgageBuyOrAccModal');

const RENT_BUY_OR_ACC_KEY = 'rentBuyOrAcc';
const COMFORT_INVOICE_BUY_OR_ACC_KEY = 'comfortInvoiceBuyOrAcc';

const regionsEnumeration = [
    { label: 'Москва и Московская область', value: 0 },
    { label: 'Санкт-Петербург и Ленинградская область', value: 1 },
];

function convertRoomForAssign(roomType?: Rooms) {
    switch (roomType) {
        case Rooms.ROOM_5_AND_MORE:
        case Rooms.ROOM_4:
        case Rooms.OPEN_PLAN:
            return Rooms.ROOM_4;
        case undefined:
        case Rooms.UNKNOWN:
            return Rooms.ROOM_1;
        default:
            return roomType;
    }
}

const typeProfitableTextReasons: Record<'buy' | 'die', [ string, string ]> = {
    buy: [
        'Получить налоговый вычет до\u00A0390\u00A0000\u00A0₽. Это поможет закрыть ипотеку досрочно, а\u00A0значит заплатить банку меньше.',
        'Рефинансировать ипотеку и\u00A0снизить ставку. Другие банки готовы снизить процентную ставку, если вы\u00A0переведете к\u00A0ним ипотеку из\u00A0своего банка.'
    ],
    die: [
        'Недвижимость может резко вырасти в\u00A0цене. Например, в\u00A02021 квартиры подорожали на\u00A022,6%, хотя ранее росли в\u00A0цене на\u00A05-7% ежегодно.',
        'Инфляция может «съесть» накопления. По\u00A0данным Росстата в\u00A02022 инфляция составила 11,94%, а\u00A0средняя ставка по\u00A0вкладам в\u00A03\u00A0кв.\u00A02022\u00A0— 5,27%.'
    ],
};

export type FormType = {
    ownAmount: number;
    propertyCost: number;
    rent: number;
    comfortInvoice: number;
    roomType?: Rooms;
    region: typeof regionsEnumeration[number]['value'];
}

export function MortgageBuyOrDieModal({
    isMobile,
    onSuccess,
    onClose,
    propertyCost,
    regionId,
    roomType,
    mortgagePercent
}: {
    isMobile?: boolean;
    onSuccess(e: React.MouseEvent<Element, MouseEvent>): void;
    onClose?(): void;
    roomType?: Rooms;
    regionId?: number;

    propertyCost: number;
    // ownAmount: number;
    mortgagePercent: number;
}) {
    const [ modalView, setModalView ] = useState<'form' | 'calcResult'>('form');
    const [ typeProfitable, setTypeProfitable ] = useState<'buy' | 'die'>();
    const [ calculatedData, setCalculatedData ] = useState<Omit<ReturnType<typeof calculateBuyOrDie>, 'typeProfitable'>>();

    // we store it here to save state between modalView
    const [ form, setForm ] = useState<FormType>({
        propertyCost,
        ownAmount: Math.ceil(propertyCost / 100 * 15),
        rent: getLocalStorageLastValue(RENT_BUY_OR_ACC_KEY),
        comfortInvoice: getLocalStorageLastValue(COMFORT_INVOICE_BUY_OR_ACC_KEY),
        roomType: convertRoomForAssign(roomType),
        region: mapRegionToSelect(regionId)
    });

    const dataLayerPush = useClassifiedOfferDataLayerPush();
    const handleCalculate = useCallback(() => {
        // after all checking
        localStorage?.setItem(COMFORT_INVOICE_BUY_OR_ACC_KEY, String(form.comfortInvoice));
        localStorage?.setItem(RENT_BUY_OR_ACC_KEY, String(form.rent));
        const { typeProfitable: calculatedTypeProfitable, ...calculatedResult } = calculateBuyOrDie({
            ...form,
            mortgagePercent,
            initialPayment: form.ownAmount
        });

        setCalculatedData(calculatedResult);
        setModalView('calcResult');
        dataLayerPush({
            event: 'up_action',
            theme: 'ipoteka',
            page_type: 'card',
            type: 'buy_or_save_calc'
        });
        document.querySelector('[class*="content-module__scrollable"]')?.scrollTo(0, 0);
        setTypeProfitable(localStorage?.getItem('buydie') as 'buy' | 'die' ?? calculatedTypeProfitable);
    }, [ form, mortgagePercent ]);
    const handleBackToForm = useCallback(() => {
        setModalView('form');
        setTypeProfitable(undefined);

        dataLayerPush({
            event: 'up_action',
            theme: 'ipoteka',
            page_type: 'card',
            type: 'buy_or_save_back'
        });
    });
    const handleSubmitMortgageReq = useCallback((e: React.MouseEvent<Element, MouseEvent>) => {
        dataLayerPush({
            event: 'up_action',
            theme: 'ipoteka',
            page_type: 'card',
            type: 'buy_or_save_rate'
        });
        onSuccess(e);
    });

    const subtitle = modalView === 'form' ? 'Что будет выгоднее: копить или купить в\u00A0ипотеку?' : 'Результаты расчёта';
    const actionText = 'Узнать свою ставку';
    const resultText = typeProfitable === 'buy' ? 'А\u00A0ещё, купив квартиру в\u00A0ипотеку вы\u00A0сможете:' : 'Стоит учитывать риски, которые сложно спрогнозировать:';

    return (<Modal
        onClose={onClose}
        closable
        mobile={isMobile}
        swipeToClose={! isMobile}
        opened
    >
        {isMobile ? <Modal.Header>Калькулятор</Modal.Header> : <Modal.Title>{subtitle}</Modal.Title>}
        <Modal.Content scrollable={isMobile}>
            {isMobile ? <Typography className={cnMrtgBuyOrDie('subtitle')} variant='h3'>{subtitle}</Typography> : null}

            {modalView === 'form' && (<>
                <MortgageBuyOrDieModalForm
                    isMobile={isMobile}
                    form={form}
                    mortgagePercent={mortgagePercent}
                    onCalculate={handleCalculate}
                    setForm={setForm}
                />
            </>)}

            {modalView === 'calcResult' && calculatedData && (<>
                <div className={cnMrtgBuyOrDie('results')}>
                    <MortgageBuyOrDieResultCard
                        type='mortgage'
                        isMobile={isMobile}
                        isProfitable={typeProfitable === 'buy'}
                        price={calculatedData.priceMortgage}
                        time={calculatedData.timeMortgage}
                    />
                    <MortgageBuyOrDieResultCard
                        type='acc'
                        isMobile={isMobile}
                        isProfitable={typeProfitable === 'die'}
                        price={calculatedData.priceAcc}
                        time={calculatedData.timeAcc}
                        isOverLimit={calculatedData.isOverLimitAcc}
                    />
                </div>
                <div className={cnMrtgBuyOrDie('results-annotation')}>
                    <Typography bold variant='primary-alone'>{resultText}</Typography>
                    {typeProfitable && (<>
                        <MortgageBuyOrDieReason
                            type={typeProfitable === 'buy' ? 'pos' : 'neg'}
                            text={typeProfitableTextReasons[typeProfitable][0]}
                        />
                        <MortgageBuyOrDieReason
                            type={typeProfitable === 'buy' ? 'pos' : 'neg'}
                            text={typeProfitableTextReasons[typeProfitable][1]}
                        />
                    </>)}
                </div>

                {/* force wrapper for correct ratio of buttons */}
                {! isMobile ? (<>
                    <div className={cnMrtgBuyOrDie('actions')}>
                        <Button
                            icon='arrow-left'
                            variant='secondary'
                            onClick={handleBackToForm}
                        />
                        <Button
                            fullWidth
                            variant={'secondary'}
                            onClick={handleSubmitMortgageReq}
                        >{actionText}</Button>
                    </div>
                    <Typography
                        className={cnMrtgBuyOrDie('disclaimer')}
                        variant='secondary-alone'
                        color='secondary'
                    >
                        Расчет ориентировочный и&nbsp;не&nbsp;является инвестиционной рекомендацией.
                    </Typography>
                </>) : null}

                {isMobile ? (<>
                    <Typography variant='secondary-alone' color='secondary'>Расчет ориентировочный и&nbsp;не&nbsp;является инвестиционной рекомендацией.</Typography>
                    <Modal.StickyActions>
                        <div className={cnMrtgBuyOrDie('actions', { isSticky: true })}>
                            <Button
                                icon='arrow-left'
                                variant='secondary'
                                onClick={handleBackToForm}
                            />
                            <Button
                                fullWidth
                                variant={'secondary'}
                                onClick={handleSubmitMortgageReq}
                            >{actionText}</Button>
                        </div>
                    </Modal.StickyActions>
                </>) : null}
            </>)}
        </Modal.Content>
    </Modal>);
}
